import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const LOCAL_STORAGE_KEY = "guest_tokens_used";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to get and clear guest usage from localStorage
function getAndClearGuestUsage(): number {
  if (typeof window === "undefined") return 0;
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  const usage = stored ? parseInt(stored, 10) : 0;
  if (usage > 0) {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
  return usage;
}

// Transfer guest usage to user account
async function transferGuestUsageToUser(userId: string) {
  const guestUsage = getAndClearGuestUsage();
  if (guestUsage <= 0) return;

  // Update the user's usage record with the guest usage
  const { error } = await supabase
    .from("user_usage")
    .update({ tokens_used: guestUsage })
    .eq("user_id", userId);

  if (error) {
    console.error("Error transferring guest usage:", error);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Transfer guest usage when user signs up
        if (event === "SIGNED_IN" && session?.user) {
          setTimeout(() => {
            transferGuestUsageToUser(session.user.id);
          }, 0);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
